/**
 * fieldLabels.js - Human-readable labels for entity fields
 * 
 * Used for:
 * - Displaying proposed changes in action cards
 * - AI context field descriptions
 * - Locked field error messages
 */

// Feature fields
export const FEATURE_FIELD_LABELS = {
  title: 'Title',
  description: 'Description',
  user_story: 'User Story',
  requirements: 'Acceptance Criteria',
  priority_level: 'Priority',
  status: 'Status',
  persona_ids: 'Linked Personas',
  assignee_id: 'Assignee',
  due_date: 'Due Date',
  effort_size: 'Effort Size',
  tag_ids: 'Tags',
  links: 'Links',
  epic_id: 'Epic',
  // KPI Alignment fields
  kpi_alignments: 'KPI Alignments',
  is_blocked: 'Blocked',
  blocker_reason: 'Blocker Reason'
};

// Goal fields
export const GOAL_FIELD_LABELS = {
  title: 'Title',
  description: 'Description',
  status: 'Status',
  target_date: 'Target Date',
  investment: 'Investment Level',
  execution_progress: 'Execution Progress',
  owner_id: 'Owner',
  kpis: 'KPIs',
  notes: 'Notes',
  // KPI sub-fields (for when AI proposes KPI changes)
  metric_name: 'Metric Name',
  metric_format: 'Format',
  direction: 'Direction',
  measurement_interval: 'Measurement Interval',
  start_value: 'Start Value',
  target_value: 'Target Value',
  current_value: 'Current Value',
  confidence: 'Confidence',
  data_points: 'Data Points',
  kpi_id: 'KPI ID'
};

// Epic fields
export const EPIC_FIELD_LABELS = {
  title: 'Title',
  description: 'Description',
  problem_statement: 'Problem Statement',
  target_quarter: 'Target Quarter',
  status: 'Status',
  persona_ids: 'Target Personas',
  owner_id: 'Owner',
  priority: 'Priority',
  // KPI Subscription fields
  strategic_goal: 'Strategic Goal',
  target_audience: 'Target Audience',
  subscribed_kpi_ids: 'Subscribed KPIs',
  initiative_id: 'Parent Initiative'
};

// Initiative fields
export const INITIATIVE_FIELD_LABELS = {
  title: 'Title',
  description: 'Description',
  hypothesis: 'Hypothesis',
  success_criteria: 'Success Criteria',
  target_quarter: 'Target Quarter',
  status: 'Status',
  owner_id: 'Owner',
  // KPI Subscription fields (N:N with Goals)
  goal_id: 'Parent Goal',           // Legacy single goal
  goal_ids: 'Linked Goals',         // New N:N relationship
  subscribed_kpi_ids: 'Subscribed KPIs',
  target_date: 'Target Date',
  date_confidence: 'Date Confidence',
  is_blocked: 'Blocked',
  blocker_reason: 'Blocker Reason',
  follow_type: 'Progress Follows',
  progress_mode: 'Progress Mode',
  manual_progress_percent: 'Manual Progress %',
  tag_ids: 'Tags',
  // Metric target sub-fields
  target_delta: 'Target Delta',
  unit: 'Unit'
};

// Persona fields
export const PERSONA_FIELD_LABELS = {
  name: 'Name',
  role: 'Role/Title',
  description: 'Description',
  goals: 'Goals',
  pain_points: 'Pain Points',
  behaviors: 'Behaviors',
  demographics: 'Demographics',
  influences: 'Influences',
  quote: 'Quote'
};

// Product fields
export const PRODUCT_FIELD_LABELS = {
  name: 'Product Name',
  high_level_vision: 'Vision',
  target_audience: 'Target Audience',
  value_proposition: 'Value Proposition',
  key_differentiators: 'Key Differentiators',
  north_star_metric: 'North Star Metric',
  strategic_pillars: 'Strategic Pillars',
  competitive_positioning: 'Competitive Positioning',
  market_size_tam: 'TAM (Total Addressable Market)',
  market_size_sam: 'SAM (Serviceable Addressable Market)',
  market_size_som: 'SOM (Serviceable Obtainable Market)',
  target_growth_rate: 'Target Growth Rate',
  market_segment_focus: 'Market Segment Focus',
  description: 'Description'
};

// Request fields
export const REQUEST_FIELD_LABELS = {
  title: 'Title',
  description: 'Description',
  type: 'Type',
  priority: 'Priority',
  stage: 'Stage',
  consideration_status: 'Consideration Status',
  linked_feature_id: 'Linked Feature',
  release_notes: 'Release Notes'
};

// KPI Alignment fields (embedded in features)
export const KPI_ALIGNMENT_FIELD_LABELS = {
  kpi_key: 'KPI',
  hypothesis: 'Support Hypothesis',
};

// Map context type to field labels
const FIELD_LABELS_MAP = {
  feature: FEATURE_FIELD_LABELS,
  goal: GOAL_FIELD_LABELS,
  epic: EPIC_FIELD_LABELS,
  initiative: INITIATIVE_FIELD_LABELS,
  persona: PERSONA_FIELD_LABELS,
  product: PRODUCT_FIELD_LABELS,
  request: REQUEST_FIELD_LABELS,
  kpi_alignment: KPI_ALIGNMENT_FIELD_LABELS,
};

/**
 * Get field labels for a context type
 * @param {string} contextType - feature, goal, epic, etc.
 * @returns {object} Field name to label mapping
 */
export const getFieldLabels = (contextType) => {
  return FIELD_LABELS_MAP[contextType] || {};
};

/**
 * Get human-readable label for a field
 * @param {string} contextType - Context type
 * @param {string} fieldName - Field name
 * @returns {string} Human-readable label
 */
export const getFieldLabel = (contextType, fieldName) => {
  const labels = getFieldLabels(contextType);
  return labels[fieldName] || fieldName;
};

/**
 * Get all field names for a context type
 * @param {string} contextType - Context type
 * @returns {string[]} Array of field names
 */
export const getFieldNames = (contextType) => {
  return Object.keys(getFieldLabels(contextType));
};

export default getFieldLabels;
